import config from '../../../config/api';
import type { EmailSchedule, ScheduleKind } from './scheduleTypes';

interface ScheduleEnvelope {
  schedule: EmailSchedule | null;
}

interface DeleteEnvelope {
  dashboard_id: string;
  deleted: boolean;
}

const buildHeaders = (userId: string, token: string | null): HeadersInit => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-User-ID': userId,
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const handleResponse = async <T>(response: Response, label: string): Promise<T> => {
  const text = await response.text();
  if (!response.ok) {
    let message = `${label} failed with HTTP ${response.status}`;
    if (text) {
      try {
        const parsed = JSON.parse(text) as { detail?: string };
        message = typeof parsed.detail === 'string' ? parsed.detail : text;
      } catch {
        message = text;
      }
    }
    const error = new Error(message) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }
  if (!text) return {} as T;
  return JSON.parse(text) as T;
};

const baseUrl = (dashboardId: string): string =>
  `${config.apiUrl}/market-monitor/dashboards/${encodeURIComponent(dashboardId)}/email-schedule`;

export const getDashboardSchedule = async (
  userId: string,
  token: string | null,
  dashboardId: string,
): Promise<EmailSchedule | null> => {
  const response = await fetch(baseUrl(dashboardId), {
    headers: buildHeaders(userId, token),
  });
  const body = await handleResponse<ScheduleEnvelope>(response, 'Get dashboard schedule');
  return body.schedule ?? null;
};

export interface UpsertScheduleArgs {
  subject: string;
  to: string;
  schedule: ScheduleKind;
}

export const upsertDashboardSchedule = async (
  userId: string,
  token: string | null,
  dashboardId: string,
  args: UpsertScheduleArgs,
): Promise<EmailSchedule> => {
  const response = await fetch(baseUrl(dashboardId), {
    method: 'PUT',
    headers: buildHeaders(userId, token),
    body: JSON.stringify({
      subject: args.subject,
      to: args.to,
      schedule: args.schedule,
    }),
  });
  return handleResponse<EmailSchedule>(response, 'Save dashboard schedule');
};

export const deleteDashboardSchedule = async (
  userId: string,
  token: string | null,
  dashboardId: string,
): Promise<boolean> => {
  const response = await fetch(baseUrl(dashboardId), {
    method: 'DELETE',
    headers: buildHeaders(userId, token),
  });
  const body = await handleResponse<DeleteEnvelope>(response, 'Delete dashboard schedule');
  return body.deleted;
};
